// src/pages/HistorialCajas.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📋 HISTORIAL DE CAJAS - ESTILO eFIT
// Consulta de todas las aperturas y cierres de caja
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useFiscal } from '../hooks/useFiscal';

const HistorialCajas = () => {
  const fiscal = useFiscal();
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    estado: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    cargarHistorial();
  }, []);

  const cargarHistorial = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.fecha_inicio) params.append("fecha_inicio", filtros.fecha_inicio);
      if (filtros.fecha_fin) params.append("fecha_fin", filtros.fecha_fin);
      if (filtros.estado) params.append("estado", filtros.estado);

      const res = await api.get(`/cajas/historial?${params.toString()}`);
      setCajas(res.data);
    } catch (error) {
      console.error("Error al cargar historial:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: fiscal.moneda?.codigo || "DOP",
    }).format(monto || 0);
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleString("es-DO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIconBox}>
              <span style={styles.headerIcon}>📋</span>
            </div>
            <div>
              <h1 style={styles.title}>Historial de Cajas</h1>
              <p style={styles.subtitle}>Todas las aperturas y cierres de caja</p>
            </div>
          </div>
          <button 
            onClick={() => navigate("/gestion-cajas")} 
            style={styles.btnVolver}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.25)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.15)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            ← Volver
          </button>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Filtros */}
        <div style={styles.filtrosCard}>
          <div style={styles.filtrosHeader}>
            <span style={styles.filtrosIcon}>🔍</span>
            <span style={styles.filtrosTitle}>Filtros de búsqueda</span>
          </div>
          <div style={styles.filtrosGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>📅 Fecha Inicio</label>
              <input
                type="date"
                value={filtros.fecha_inicio}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_inicio: e.target.value })
                }
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>📅 Fecha Fin</label>
              <input
                type="date"
                value={filtros.fecha_fin}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_fin: e.target.value })
                }
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>📌 Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                style={styles.select}
              >
                <option value="">Todos</option>
                <option value="abierta">🟢 Abierta</option>
                <option value="cerrada">🔒 Cerrada</option>
              </select>
            </div>

            <div style={styles.filtrosActions}>
              <button 
                onClick={cargarHistorial} 
                style={styles.btnFiltrar}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
                }}
              >
                🔍 Filtrar
              </button>
              <button
                onClick={() => {
                  setFiltros({ fecha_inicio: "", fecha_fin: "", estado: "" });
                  setTimeout(() => cargarHistorial(), 100);
                }}
                style={styles.btnLimpiar}
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Lista de Cajas */}
        {cajas.length === 0 ? (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}>📭</div>
            <p style={styles.emptyText}>No se encontraron cajas</p>
            <p style={styles.emptyHint}>Intenta con otros filtros de búsqueda</p>
          </div>
        ) : (
          <div style={styles.cajasGrid}>
            {cajas.map((caja, index) => {
              const diferencia = parseFloat(caja.diferencia || 0);

              return (
                <div
                  key={caja.id}
                  style={{
                    ...styles.cajaCard,
                    animationDelay: `${index * 0.05}s`,
                  }}
                  onClick={() => navigate(`/detalle-caja/${caja.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 16px 40px rgba(30, 58, 138, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                  }}
                >
                  <div style={styles.cajaHeader}>
                    <div
                      style={{
                        ...styles.estadoBadge,
                        background: caja.estado === "abierta" 
                          ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                          : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        color: caja.estado === "abierta" ? "#065f46" : "#475569",
                        border: `1px solid ${caja.estado === "abierta" ? '#6ee7b7' : '#cbd5e1'}`,
                      }}
                    >
                      {caja.estado === "abierta" ? "🟢 Abierta" : "🔒 Cerrada"}
                    </div>
                    <h3 style={styles.cajaCodigo}>{caja.codigo}</h3>
                  </div>

                  <div style={styles.cajaInfo}>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>👤 Cajero</span>
                      <span style={styles.infoValue}>
                        {caja.usuario_apertura_nombre}
                      </span>
                    </div>

                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>📅 Apertura</span>
                      <span style={styles.infoValue}>
                        {formatearFecha(caja.fecha_apertura)}
                      </span>
                    </div>

                    {caja.fecha_cierre && (
                      <div style={styles.infoRow}>
                        <span style={styles.infoLabel}>🔒 Cierre</span>
                        <span style={styles.infoValue}>
                          {formatearFecha(caja.fecha_cierre)}
                        </span>
                      </div>
                    )}

                    <div style={styles.divider}></div>

                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>💵 Monto Inicial</span>
                      <span style={styles.infoValue}>
                        {formatearMoneda(caja.monto_inicial)}
                      </span>
                    </div>

                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>📊 Total Ventas</span>
                      <span style={{...styles.infoValue, color: '#059669', fontWeight: '700'}}>
                        {formatearMoneda(caja.total_ventas)} ({caja.num_ventas || 0})
                      </span>
                    </div>

                    {caja.estado === "cerrada" && (
                      <>
                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>✅ Monto Esperado</span>
                          <span style={styles.infoValue}>
                            {formatearMoneda(caja.monto_esperado)}
                          </span>
                        </div>

                        <div style={styles.infoRow}>
                          <span style={styles.infoLabel}>💰 Monto Real</span>
                          <span style={styles.infoValue}>
                            {formatearMoneda(caja.monto_real)}
                          </span>
                        </div>

                        <div style={styles.divider}></div>

                        <div style={{
                          ...styles.diferenciaBox,
                          background: diferencia > 0 
                            ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                            : diferencia < 0 
                              ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                              : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          borderColor: diferencia > 0 ? '#10b981' : diferencia < 0 ? '#ef4444' : '#3b82f6',
                        }}>
                          <span style={styles.diferenciaLabel}>Diferencia</span>
                          <span style={{
                            ...styles.diferenciaMonto,
                            color: diferencia > 0 ? '#059669' : diferencia < 0 ? '#dc2626' : '#1e3a8a',
                          }}>
                            {diferencia > 0 && "+"}
                            {formatearMoneda(diferencia)}
                            {diferencia > 0 && " 💰"}
                            {diferencia < 0 && " ⚠️"}
                            {diferencia === 0 && " ✅"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={styles.cajaFooter}>
                    <button style={styles.btnVerDetalle}>
                      👁️ Ver Detalle
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Estilos de animación */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT - PALETA AZUL CORPORATIVA
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  },
  loadingContent: {
    textAlign: "center",
    color: "white",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid rgba(255,255,255,0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 20px",
  },
  loadingText: {
    fontSize: "16px",
    opacity: "0.9",
  },

  // Header
  header: {
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    padding: "28px 40px",
    boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1400px",
    margin: "0 auto",
    flexWrap: "wrap",
    gap: "20px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  headerIconBox: {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  headerIcon: {
    fontSize: "36px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "800",
    color: "white",
    margin: "0",
  },
  subtitle: {
    fontSize: "15px",
    color: "rgba(255, 255, 255, 0.85)",
    margin: "6px 0 0 0",
  },
  btnVolver: {
    padding: "14px 28px",
    background: "rgba(255,255,255,0.15)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backdropFilter: "blur(10px)",
  },

  // Main Content
  mainContent: {
    padding: "32px 40px",
    maxWidth: "1400px",
    margin: "0 auto",
  },

  // Filtros
  filtrosCard: {
    background: "white",
    borderRadius: "18px",
    padding: "24px",
    marginBottom: "28px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  filtrosHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  filtrosIcon: {
    fontSize: "20px",
  },
  filtrosTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
  },
  filtrosGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    alignItems: "end",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "8px",
  },
  input: {
    padding: "12px 14px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    background: "#f8fafc",
  },
  select: {
    padding: "12px 14px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    background: "#f8fafc",
    cursor: "pointer",
  },
  filtrosActions: {
    display: "flex",
    gap: "10px",
  },
  btnFiltrar: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  },
  btnLimpiar: {
    padding: "12px 24px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Empty State
  emptyCard: {
    background: "white",
    borderRadius: "20px",
    padding: "80px 40px",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  },
  emptyIcon: {
    fontSize: "72px",
    marginBottom: "20px",
    opacity: "0.3",
  },
  emptyText: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#475569",
    margin: "0 0 8px 0",
  },
  emptyHint: {
    fontSize: "14px",
    color: "#94a3b8",
    margin: "0",
  },

  // Cajas Grid
  cajasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "24px",
  },
  cajaCard: {
    background: "white",
    borderRadius: "20px",
    padding: "24px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid rgba(59, 130, 246, 0.1)",
    animation: "fadeInUp 0.4s ease forwards",
    opacity: 0,
  },
  cajaHeader: {
    marginBottom: "20px",
  },
  estadoBadge: {
    display: "inline-block",
    padding: "8px 16px",
    borderRadius: "24px",
    fontSize: "13px",
    fontWeight: "700",
    marginBottom: "12px",
  },
  cajaCodigo: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#1e293b",
    margin: "0",
  },
  cajaInfo: {
    marginBottom: "20px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    fontSize: "14px",
  },
  infoLabel: {
    color: "#64748b",
  },
  infoValue: {
    fontWeight: "600",
    color: "#1e293b",
  },
  divider: {
    height: "2px",
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
    margin: "16px 0",
  },
  diferenciaBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px",
    borderRadius: "12px",
    border: "2px solid",
    marginTop: "12px",
  },
  diferenciaLabel: {
    fontSize: "14px",
    fontWeight: "600",
  },
  diferenciaMonto: {
    fontSize: "18px",
    fontWeight: "800",
  },
  cajaFooter: {
    display: "flex",
    justifyContent: "center",
  },
  btnVerDetalle: {
    width: "100%",
    padding: "14px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  },
};

export default HistorialCajas;